import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './App.css';

// Backend API base URL - automatically detects environment
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://lead-scoring-api.sada-6e2.workers.dev';

const App = () => {
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [scoring, setScoring] = useState(false);
    const [scores, setScores] = useState({});
    const [scoringDetails, setScoringDetails] = useState({});
    const [modelInfo, setModelInfo] = useState(null);
    const [testResults, setTestResults] = useState(null);

    // Debug logging
    useEffect(() => {
        console.log('🔍 App Debug Info:');
        console.log('API_BASE_URL:', API_BASE_URL);
        console.log('REACT_APP_API_URL:', process.env.REACT_APP_API_URL);
        console.log('NODE_ENV:', process.env.NODE_ENV);
    }, []);

    // Clear error after 5 seconds
    useEffect(() => {
        if (error) {
            const timer = setTimeout(() => setError(''), 5000);
            return () => clearTimeout(timer);
        }
    }, [error]);

    // Fetch leads from Salesforce via our API
    const fetchLeads = async () => {
        setLoading(true);
        setError('');
        setTestResults(null); // Clear test results when loading real leads
        
        try {
            console.log('🔄 Fetching leads from:', `${API_BASE_URL}/api/leads`);
            const response = await axios.get(`${API_BASE_URL}/api/leads`);
            console.log('✅ Leads response:', response.data);
            
            if (response.data.records) {
                setLeads(response.data.records);
                setModelInfo(response.data.metadata);
                console.log(`✅ Loaded ${response.data.records.length} leads`);
            } else {
                throw new Error('No records found in response');
            }
        } catch (err) {
            console.error('❌ Error fetching leads:', err);
            setError(`Failed to fetch leads: ${err.response?.data?.error || err.message}`);
        } finally {
            setLoading(false);
        }
    };

    // Score leads using enhanced AI model
    const scoreLeads = async () => {
        if (leads.length === 0) {
            setError('No leads to score. Please fetch leads first.');
            return;
        }

        setScoring(true);
        setError('');

        try {
            console.log('🧠 Scoring leads with enhanced AI model...');
            const response = await axios.post(`${API_BASE_URL}/score-leads`, leads);
            console.log('✅ Scoring response:', response.data);
            
            setScores(response.data.scores || {});
            setScoringDetails(response.data.details || {});
            setModelInfo(response.data.modelInfo);
            
            console.log(`✅ Scored ${Object.keys(response.data.scores || {}).length} leads`);
        } catch (err) {
            console.error('❌ Error scoring leads:', err);
            setError(`Failed to score leads: ${err.response?.data?.error || err.message}`);
        } finally {
            setScoring(false);
        }
    };

    // Test enhanced AI model with sample data
    const testEnhancedScoring = async () => {
        setLoading(true);
        setError('');
        setTestResults(null); // Clear existing results first

        try {
            console.log('🧪 Testing enhanced AI scoring model...');
            console.log('📡 Making request to:', `${API_BASE_URL}/test-scoring`);
            
            const response = await axios.post(`${API_BASE_URL}/test-scoring`, {}, {
                timeout: 10000, // 10 second timeout
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            
            console.log('✅ Test response received:', response);
            console.log('📊 Test data:', response.data);
            
            // Accept ANY valid response and let the UI handle it
            if (response.data) {
                console.log('✅ Setting test results with data:', response.data);
                console.log('🔍 Response keys:', Object.keys(response.data));
                console.log('🔍 Has testResults?', !!response.data.testResults);
                console.log('🔍 Has test_leads?', !!response.data.test_leads);
                console.log('🔍 Has message?', !!response.data.message);
                
                setTestResults(response.data);
                console.log('✅ Test results set successfully');
            } else {
                console.warn('⚠️ Empty response data');
                throw new Error('Empty response from test API');
            }
        } catch (err) {
            console.error('❌ Error testing scoring:', err);
            console.error('📍 Error details:', {
                message: err.message,
                response: err.response?.data,
                status: err.response?.status,
                url: err.config?.url
            });
            setError(`Failed to test scoring: ${err.response?.data?.error || err.message}`);
        } finally {
            setLoading(false);
        }
    };

    // Get score category styling
    const getScoreBadge = (score, details) => {
        const category = details?.category || getScoreCategory(score);
        const badgeClass = {
            'Hot': 'badge-hot',
            'Warm': 'badge-warm', 
            'Cold': 'badge-cold',
            'Ice': 'badge-ice'
        }[category.name] || 'badge-default';

        return (
            <span className={`score-badge ${badgeClass}`}>
                {category.emoji} {score} - {category.name}
            </span>
        );
    };

    // Fallback score categorization
    const getScoreCategory = (score) => {
        if (score >= 80) return { name: 'Hot', emoji: '🔥', action: 'Immediate follow-up' };
        if (score >= 65) return { name: 'Warm', emoji: '🟡', action: 'Contact within 3 days' };
        if (score >= 45) return { name: 'Cold', emoji: '🔵', action: 'Nurture campaign' };
        return { name: 'Ice', emoji: '❄️', action: 'Quarterly review' };
    };

    // Format factor breakdown for display
    const formatFactorBreakdown = (factors) => {
        if (!factors) return null;
        
        return Object.entries(factors).map(([factor, score]) => (
            <div key={factor} className="factor-item">
                <span className="factor-name">{factor.charAt(0).toUpperCase() + factor.slice(1)}:</span>
                <span className="factor-score">{Math.round(score)}</span>
            </div>
        ));
    };

    return (
        <div className="app">
            <header className="app-header">
                <h1>🤖 AI Lead Scoring Dashboard</h1>
                {modelInfo && (
                    <div className="model-info">
                        <span className="model-badge">
                            Model v{modelInfo.version} • {modelInfo.totalFactors || modelInfo.factors} Factors • 
                            {modelInfo.algorithm && ` ${modelInfo.algorithm.replace(/_/g, ' ')}`}
                        </span>
                    </div>
                )}
            </header>

            <main className="app-main">
                {/* Action Buttons */}
                <div className="action-buttons">
                    <button 
                        onClick={fetchLeads} 
                        disabled={loading || scoring}
                        className="btn btn-primary"
                    >
                        {loading ? '⏳ Loading...' : '📊 Load Leads from Salesforce'}
                    </button>

                    <button 
                        onClick={scoreLeads} 
                        disabled={scoring || leads.length === 0 || loading}
                        className="btn btn-secondary"
                    >
                        {scoring ? '🧠 AI Scoring...' : '🎯 Score Leads with AI'}
                    </button>

                    <button 
                        onClick={testEnhancedScoring}
                        disabled={loading || scoring}
                        className="btn btn-accent"
                    >
                        {loading ? '⏳ Testing...' : '🧪 Test Enhanced AI Model'}
                    </button>
                </div>

                {/* Debug Info */}
                <div className="debug-info" style={{fontSize: '0.8rem', color: '#666', marginBottom: '1rem'}}>
                    API: {API_BASE_URL} | Test Results: {testResults ? '✅ Loaded' : '❌ None'} | Leads: {leads.length}
                </div>

                {/* Error Display */}
                {error && (
                    <div className="error-message">
                        <span className="error-icon">⚠️</span>
                        {error}
                    </div>
                )}

                {/* Test Results */}
                {testResults && (
                    <div className="test-results">
                        <h2>🧪 AI Model Test Results</h2>
                        
                        {/* Debug: Show raw response structure */}
                        <div className="debug-response" style={{background: '#f0f0f0', padding: '1rem', marginBottom: '1rem', fontSize: '0.8rem'}}>
                            <details>
                                <summary><strong>🔍 Debug - Raw API Response (click to expand)</strong></summary>
                                <pre style={{marginTop: '0.5rem', fontSize: '0.7rem', overflow: 'auto', maxHeight: '200px'}}>
                                    {JSON.stringify(testResults, null, 2)}
                                </pre>
                            </details>
                        </div>

                        {/* Enhanced Format (New) */}
                        {testResults.testResults?.summary && (
                            <>
                                <div className="test-summary">
                                    <div className="summary-grid">
                                        <div className="summary-card">
                                            <h3>📊 Summary</h3>
                                            <p><strong>Total Leads:</strong> {testResults.testResults.summary.totalLeads}</p>
                                            <p><strong>Average Score:</strong> {testResults.testResults.summary.averageScore}</p>
                                            <p><strong>Average Confidence:</strong> {testResults.testResults.summary.averageConfidence}%</p>
                                        </div>
                                        
                                        <div className="summary-card">
                                            <h3>🔥 Score Distribution</h3>
                                            <p><span className="badge-hot">🔥 Hot:</span> {testResults.testResults.summary.hotLeads}</p>
                                            <p><span className="badge-warm">🟡 Warm:</span> {testResults.testResults.summary.warmLeads}</p>
                                            <p><span className="badge-cold">🔵 Cold:</span> {testResults.testResults.summary.coldLeads}</p>
                                            <p><span className="badge-ice">❄️ Ice:</span> {testResults.testResults.summary.iceLeads}</p>
                                        </div>
                                        
                                        <div className="summary-card">
                                            <h3>🎯 AI Factors</h3>
                                            {testResults.factorsUsed && testResults.factorsUsed.map((factor, index) => (
                                                <p key={index} className="factor-weight">{factor}</p>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                {testResults.insights && (
                                    <div className="test-insights">
                                        <h3>💡 AI Insights</h3>
                                        {testResults.insights.map((insight, index) => (
                                            <div key={index} className="insight-item">{insight}</div>
                                        ))}
                                    </div>
                                )}

                                {testResults.testResults.detailedBreakdown && (
                                    <div className="detailed-breakdown">
                                        <h3>📋 Detailed Lead Analysis</h3>
                                        <div className="leads-grid">
                                            {Object.entries(testResults.testResults.detailedBreakdown).map(([leadId, details]) => (
                                                <div key={leadId} className="lead-card">
                                                    <div className="lead-header">
                                                        <h4>{details.profile?.name || leadId}</h4>
                                                        <div className="score-info">
                                                            {getScoreBadge(details.score, details)}
                                                            <span className="confidence">({details.confidence}% confidence)</span>
                                                        </div>
                                                    </div>
                                                    <div className="lead-details">
                                                        <p><strong>Company:</strong> {details.profile?.company || 'N/A'}</p>
                                                        <p><strong>Title:</strong> {details.profile?.title || 'N/A'}</p>
                                                        <p><strong>Industry:</strong> {details.profile?.industry || 'N/A'}</p>
                                                        <p><strong>Employees:</strong> {details.profile?.employees?.toLocaleString() || 'N/A'}</p>
                                                        <p><strong>Action:</strong> {details.category?.action || 'N/A'}</p>
                                                    </div>
                                                    {details.factors && (
                                                        <div className="factor-breakdown">
                                                            <h5>Factor Scores:</h5>
                                                            <div className="factors-grid">
                                                                {formatFactorBreakdown(details.factors)}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </>
                        )}

                        {/* Legacy Format (Old) */}
                        {testResults.test_leads && testResults.scores && !testResults.testResults && (
                            <div className="legacy-test-results">
                                <div className="summary-card" style={{marginBottom: '2rem'}}>
                                    <h3>⚠️ Legacy Test Results</h3>
                                    <p><strong>Message:</strong> {testResults.message}</p>
                                    <p><strong>Test Leads:</strong> {testResults.test_leads?.length || 0}</p>
                                    <p><em>This is using the old API format. Consider updating the Worker for enhanced features.</em></p>
                                </div>

                                <div className="simple-leads-table">
                                    <h3>📊 Test Leads & Scores</h3>
                                    <table className="leads-table">
                                        <thead>
                                            <tr>
                                                <th>Lead ID</th>
                                                <th>Industry</th>
                                                <th>Employees</th>
                                                <th>Score</th>
                                                <th>Category</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {testResults.test_leads?.map((lead) => {
                                                const score = testResults.scores[lead.Id];
                                                const category = getScoreCategory(score);
                                                return (
                                                    <tr key={lead.Id}>
                                                        <td>{lead.Id}</td>
                                                        <td>{lead.Industry}</td>
                                                        <td>{lead.NumberOfEmployees?.toLocaleString()}</td>
                                                        <td>{getScoreBadge(score, null)}</td>
                                                        <td>{category.action}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* Unknown Format */}
                        {!testResults.testResults && !testResults.test_leads && (
                            <div className="unknown-format">
                                <h3>⚠️ Unknown Response Format</h3>
                                <p>The API returned an unexpected format. Check the debug section above to see the raw response.</p>
                                <div style={{background: '#fff3cd', border: '1px solid #ffeaa7', padding: '1rem', borderRadius: '4px'}}>
                                    <strong>Possible Issues:</strong>
                                    <ul>
                                        <li>Worker not deployed with enhanced test endpoint</li>
                                        <li>API returning error response</li>
                                        <li>CORS or network issues</li>
                                    </ul>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Salesforce Leads */}
                {leads.length > 0 && (
                    <div className="leads-section">
                        <h2>📊 Salesforce Leads ({leads.length})</h2>
                        
                        <div className="leads-table-container">
                            <table className="leads-table">
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Company</th>
                                        <th>Industry</th>
                                        <th>Employees</th>
                                        <th>Title</th>
                                        <th>AI Score</th>
                                        <th>Confidence</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {leads.map((lead) => {
                                        const score = scores[lead.Id];
                                        const details = scoringDetails[lead.Id];
                                        const category = details?.category || (score ? getScoreCategory(score) : null);
                                        
                                        return (
                                            <tr key={lead.Id} className={score ? `lead-row score-${category?.name?.toLowerCase()}` : 'lead-row'}>
                                                <td>
                                                    <div className="lead-name">
                                                        {lead.FirstName} {lead.LastName}
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="company-info">
                                                        {lead.Company}
                                                        {lead.NumberOfEmployees && (
                                                            <small> ({lead.NumberOfEmployees.toLocaleString()} employees)</small>
                                                        )}
                                                    </div>
                                                </td>
                                                <td>{lead.Industry || 'N/A'}</td>
                                                <td>{lead.NumberOfEmployees?.toLocaleString() || 'N/A'}</td>
                                                <td>{lead.Title || 'N/A'}</td>
                                                <td>
                                                    {score ? getScoreBadge(score, details) : (
                                                        <span className="not-scored">Not scored</span>
                                                    )}
                                                </td>
                                                <td>
                                                    {details?.confidence ? (
                                                        <span className="confidence-score">{details.confidence}%</span>
                                                    ) : 'N/A'}
                                                </td>
                                                <td>
                                                    {category ? (
                                                        <span className="action-item">{category.action}</span>
                                                    ) : 'N/A'}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* No Data State */}
                {leads.length === 0 && !loading && !testResults && (
                    <div className="no-data">
                        <div className="no-data-content">
                            <h3>🚀 Welcome to AI Lead Scoring</h3>
                            <p>Get started by testing the enhanced AI model or loading leads from Salesforce:</p>
                            <ul>
                                <li><strong>🧪 Test Enhanced AI Model:</strong> See the 8-factor scoring system in action</li>
                                <li><strong>📊 Load Leads:</strong> Fetch real leads from your Salesforce org</li>
                                <li><strong>🎯 Score Leads:</strong> Apply AI scoring to your leads</li>
                            </ul>
                        </div>
                    </div>
                )}
            </main>

            <footer className="app-footer">
                <p>🤖 Powered by Enhanced AI Lead Scoring • Cloudflare Workers • React</p>
                <p>
                    <a href={`${API_BASE_URL}/docs`} target="_blank" rel="noopener noreferrer">
                        📚 API Documentation
                    </a>
                </p>
            </footer>
        </div>
    );
};

export default App;